import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Col, Row } from 'react-bootstrap'
import { compose } from 'redux'
import useFeatureFlag from 'shared/hooks/useFeatureFlag/useFeatureFlag'
import { useCatalogCategoryProducts } from '../../api/hooks'
import { withActiveSortParams, withActiveProductFilters } from '../../reducers'

import CategoryHero from './category-hero'
import FacetsBar from '../Filters/components/FacetsBar/FacetsBar'
import ProductGrid, { mapHeroesToProductCards } from '../product-grid'
import { ProductFilters } from '../product-filters'
import ShopByGuides from '../shop-by-guides'
import FrequentlyAskedQuestions from '../FrequentlyAskedQuestions/FrequentlyAskedQuestions'
import { parseFilterQueryParams } from 'store/lib'

export const HeroImage = ({ className, category }) => (
  <CategoryHero
    className={className}
    imageUrl={category?.teaser?.imageUrl}
    teaser={category?.teaser?.text}
    teaserLinkText={category?.teaserLinkText}
    teaserLinkUrl={category?.teaserLinkUrl}
    title={category.title}
  />
)

HeroImage.propTypes = {
  className: PropTypes.string,
  category: PropTypes.object.isRequired,
}

HeroImage.defaultProps = {
  className: '',
}

// export base so existing tests pass
export const LeafCategoryPageBase = ({
  category,
  location,
  sortParams,
  productFilters,
}) => {
  const { flagValue: inFacetsFeature } = useFeatureFlag(
    '24-b-ff-shop-facets-and-filters',
    false
  )
  const [filtersToApply, setFiltersToApply] = useState(() =>
    inFacetsFeature ? parseFilterQueryParams(location.search) : productFilters
  )

  useEffect(() => {
    const newFilters = inFacetsFeature
      ? parseFilterQueryParams(location.search)
      : productFilters
    setFiltersToApply(newFilters)
  }, [location.search, productFilters, inFacetsFeature])

  const { data, isLoading } = useCatalogCategoryProducts(category.urlSlug, {
    heroes: true,
    filters: filtersToApply,
    orderBy: {
      key: sortParams?.sort,
      direction: sortParams?.order,
    },
    inFacetsFeature,
  })

  const productCards = isLoading
    ? null
    : data.heroProducts.map((p) => mapHeroesToProductCards(p))
  const heroProducts = isLoading ? null : data.heroProducts
  const facets = isLoading ? null : data.facets

  const teaserImageUrl = category?.teaser?.imageUrl
  const teaserText = category?.teaser?.text

  return (
    <div>
      <Row>
        <Col sm={teaserImageUrl || !category.isSortable ? 12 : 8} xs={12}>
          <HeroImage category={category} />
        </Col>
      </Row>
      {!teaserImageUrl && <hr className="mts mbm" />}
      {inFacetsFeature && facets ? (
        <FacetsBar categorySlug={category.urlSlug} facets={facets} />
      ) : (
        heroProducts && <ProductFilters products={heroProducts} />
      )}
      {teaserText && !teaserImageUrl && (
        <div className="hidden-xs h6">{teaserText}</div>
      )}
      <ProductGrid
        activeSlugSet={location?.state?.slugSet}
        guides={category.guides}
        productCards={productCards}
      />
      <ShopByGuides guides={category.guides} />
      {category.frequentlyAskedQuestions &&
        category.frequentlyAskedQuestions.length > 0 && (
          <div className="mtxl">
            <FrequentlyAskedQuestions
              headerVariant="secondary"
              questions={category.frequentlyAskedQuestions}
            />
          </div>
        )}
    </div>
  )
}

LeafCategoryPageBase.propTypes = {
  category: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  sortParams: PropTypes.object.isRequired,
  productFilters: PropTypes.object.isRequired,
}

export const LeafCategoryPage = compose(
  withActiveProductFilters(),
  withActiveSortParams()
)(LeafCategoryPageBase)
